/**
 * UpdatePromptSheet - "A new version of Worthly is here!" pop-up.
 *
 * Shown by App.js when utils/updatePrompt.js finds a newer App Store version
 * (and from the admin preview button in Settings). Theme aware (pink/warm/blue),
 * same colours as the review ask screen.
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const SERIF = "'DM Serif Display', serif";
const SANS = "'DM Sans', sans-serif";
const INK = '#1F2937';
const MUTED = '#6B7280';

const PALETTES = {
  pink: { btnBg: '#E4D8F4', btnFg: '#6A4FA3', shadow: '122,95,176', card: 'linear-gradient(160deg, #FAE3EC, #E9E1F5)' },
  warm: { btnBg: '#DCE7D6', btnFg: '#2D5A3D', shadow: '45,90,61', card: 'linear-gradient(160deg, #F7E7D9, #E9EFD9)' },
  blue: { btnBg: '#D6DEF7', btnFg: '#3A4DA8', shadow: '58,77,168', card: 'linear-gradient(160deg, #E1E8FA, #E4E1F5)' }
};

const UpdatePromptSheet = ({ theme = 'pink', onUpdate, onLater }) => {
  const P = PALETTES[theme] || PALETTES.pink;
  const rootRef = useRef(null);

  // Lock the page behind the pop-up while it's open, and put it back after.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    // iOS still scrolls the page from a swipe unless the swipe itself is stopped.
    const el = rootRef.current;
    const stop = (e) => e.preventDefault();
    if (el) el.addEventListener('touchmove', stop, { passive: false });
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      if (el) el.removeEventListener('touchmove', stop);
    };
  }, []);

  return createPortal(
    <div
      ref={rootRef}
      className="fixed inset-0 flex items-center justify-center animate-in fade-in duration-300"
      style={{ zIndex: 9999, padding: 16, fontFamily: SANS }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-prompt-title"
    >
      <div className="absolute inset-0" style={{ background: 'rgba(20,16,30,0.42)' }} />
      <div
        className="relative w-full text-center animate-in zoom-in-95 duration-300"
        style={{ maxWidth: 380, background: '#FFFFFF', borderRadius: 28, padding: '24px 20px 12px', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
      >
        {/* Logo */}
        <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0 18px' }}>
          <img
            src="/default-icon.png"
            alt="Worthly"
            style={{ width: 80, height: 80, borderRadius: 20, display: 'block', boxShadow: `0 10px 24px rgba(${P.shadow},0.22)` }}
          />
        </div>

        <h2 id="update-prompt-title" style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 26, lineHeight: 1.15, color: INK, margin: '0 0 8px' }}>
          A new version of Worthly is here!
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: INK, margin: '0 8px 22px' }}>
          Updating gets you the latest features and fixes, and keeps everything running smoothly. It only takes a minute on the App Store.
        </p>

        <button
          type="button"
          onClick={onUpdate}
          style={{
            display: 'block', width: '100%', border: 0, borderRadius: 999, padding: 16,
            background: P.btnBg, color: P.btnFg, fontFamily: SANS, fontSize: 17, fontWeight: 600,
            boxShadow: `inset 0 -2px 0 rgba(${P.shadow},0.10)`,
          }}
        >
          Update now
        </button>
        <button
          type="button"
          onClick={onLater}
          style={{ display: 'block', width: '100%', minHeight: 44, border: 0, background: 'transparent', color: MUTED, fontFamily: SANS, fontSize: 16, fontWeight: 500, padding: '14px 0 6px' }}
        >
          Later
        </button>
      </div>
    </div>,
    document.body
  );
};

export default UpdatePromptSheet;
